// store/leave/leaveActions.ts
import {
    LEAVE_DETAILS_SUCCESS,
    LEAVE_DETAILS_FAILURE,
    LEAVE_DETAILS_RESET,
    LEAVE_SET_LOADING,
    LEAVE_SET_ERROR,
    LEAVE_CLEAR_ERROR,
    LEAVE_MANAGE_FAILURE,
    LEAVE_MANAGE_SUCCESS,
    LEAVE_MANAGE_REQUEST
} from "./LeaveActionType";

const apiConfig = window as any;

/* ------------------- BASE API URL ------------------- */

const BASE_API_URL =
    apiConfig.BASE_API_URL?.charAt(apiConfig.BASE_API_URL.length - 1) === "/"
        ? apiConfig.BASE_API_URL.substring(0, apiConfig.BASE_API_URL.length - 1)
        : apiConfig.BASE_API_URL;

console.log("Leave API URL:", BASE_API_URL);

const BasicAuthentication = apiConfig.BasicAuthentication;

/* ------------------- HEADERS ------------------- */

const postAuthorizedHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(BasicAuthentication
        ? { Authorization: "Basic " + btoa(BasicAuthentication) }
        : {})
};

/* ============================================================
   INTERFACES
============================================================ */

export interface LeaveFilterRequest {
    EmployeeCode?: string;
    Status?: string;
    LeaveType?: string;
    FromDate?: string;
    ToDate?: string;
}

export interface LeaveRecord {
    employeeExists: boolean;
    employeeName: string;
    employeeDepartment: string;
    employeeDesignation: string;
    message: string;
    id: number;
    leaveRequestId: string;
    employeeId: number;
    employeeCode: string;
    leaveType: string;
    fromDate: string;
    toDate: string;
    days: number;
    status: string;
    appliedOn: string;
    reason: string;
    createdOn: string;
    updatedOn: string;
    updatedBy: string | null;
}

export interface LeaveApiResponse {
    isSuccess: boolean;
    message: string;
    accessedOn: string | null;
    records: LeaveRecord[];
}
export interface ManageLeaveRequest {
    appliedOn?: string;
    days: number;
    employeeCode: string;
    fromDate: string;
    leaveRequestId?: string;
    leaveType: string;
    operation: string; // "I" for Insert, "U" for Update, "D" for Delete
    status?: string;
    toDate: string;
    updatedBy: string;
}

export interface ManageLeaveResponse {
    success: boolean;
    operation: string;
    message: string;
    lastUpdated: string;
}


/* ============================================================
   GET LEAVE DETAILS
============================================================ */

export const GetLeaveDetails = 
    (employeeCode?: string) => async (dispatch: any) => {
        dispatch({ type: LEAVE_SET_LOADING, payload: true });

        try {
            const params = new URLSearchParams();
            if (employeeCode) {
                params.append('employeeCode', employeeCode);
            }
            
            const queryString = params.toString();
            const url = `${BASE_API_URL}/LeaveDetails${queryString ? `?${queryString}` : ''}`;

            console.log("Fetching leave details from:", url);

            const response = await fetch(url, {
                method: "GET",
                headers: postAuthorizedHeaders
            });

            const data: LeaveApiResponse = await response.json();

            console.log("Leave API response:", data);

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            if (data?.isSuccess) {
                const records = data.records || [];

                dispatch({
                    type: LEAVE_DETAILS_SUCCESS,
                    payload: records
                });

                return { records };
            }

            dispatch({
                type: LEAVE_DETAILS_FAILURE,
                payload: data.message || "Failed to fetch leave details"
            });

            return { records: [] };
        } catch (err) {
            console.error("GetLeaveDetails error:", err);

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            dispatch({
                type: LEAVE_SET_ERROR,
                payload: {
                    success: false,
                    message: "Failed to fetch leave details",
                    error: err
                }
            });

            return { records: [] };
        }
    };

   export const ManageLeave = 
    (leaveData: ManageLeaveRequest) => async (dispatch: any) => {
        dispatch({ type: LEAVE_MANAGE_REQUEST, payload: { operation: leaveData.operation } });
        dispatch({ type: LEAVE_SET_LOADING, payload: true });

        try {
            const url = `${BASE_API_URL}/ManageLeave`;

            console.log("Managing leave request to:", url);
            console.log("Request payload:", leaveData);

            const response = await fetch(url, {
                method: "POST",
                headers: postAuthorizedHeaders,
                body: JSON.stringify(leaveData)
            });

            const data: ManageLeaveResponse = await response.json();

            console.log("Manage leave API response:", data);

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            if (data?.success) {
                dispatch({
                    type: LEAVE_MANAGE_SUCCESS,
                    payload: {
                        message: data.message,
                        operation: data.operation,
                        lastUpdated: data.lastUpdated
                    }
                });

                // Refresh the leave list
                if (leaveData.employeeCode) {
                    setTimeout(() => {
                        dispatch(GetLeaveDetails(leaveData.employeeCode) as any);
                    }, 100);
                }

                return { 
                    success: true, 
                    data,
                    message: data.message 
                };
            }

            const errorPayload = {
                success: false,
                message: data.message || "Failed to process leave request",
                error: data
            };

            dispatch({
                type: LEAVE_SET_ERROR,
                payload: errorPayload
            });

            dispatch({
                type: LEAVE_MANAGE_FAILURE,
                payload: errorPayload
            });

            return { 
                success: false, 
                message: data.message || "Failed to process leave request" 
            };
        } catch (err) {
            console.error("ManageLeave error:", err);

            dispatch({ type: LEAVE_SET_LOADING, payload: false });

            const errorPayload = {
                success: false,
                message: "Failed to process leave request",
                error: err
            };

            dispatch({
                type: LEAVE_SET_ERROR,
                payload: errorPayload
            });

            dispatch({
                type: LEAVE_MANAGE_FAILURE,
                payload: errorPayload
            });

            return { 
                success: false, 
                message: "Failed to process leave request" 
            };
        }
    };

/* ============================================================
   FILTER ACTIONS
============================================================ */

export const SetLeaveFilters = (filters: LeaveFilterRequest) => ({
    type: "LEAVE_SET_FILTERS",
    payload: filters
});

export const ClearLeaveFilters = () => ({
    type: "LEAVE_CLEAR_FILTERS"
});

/* ============================================================
   SELECT LEAVE RECORD
============================================================ */

export const SetSelectedLeave = (leaveRecord: LeaveRecord | null) => ({
    type: "LEAVE_SET_SELECTED",
    payload: leaveRecord
});

/* ============================================================
   RESET LEAVE STATE
============================================================ */

export const ResetLeaveDetails = () => (dispatch: any) => {
    dispatch({ type: LEAVE_DETAILS_RESET });
};

/* ============================================================
   CLEAR ERROR
============================================================ */

export const ClearLeaveError = () => ({
    type: LEAVE_CLEAR_ERROR
});

// store/leave/leaveActions.ts - Add this function



